import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Star, MapPin, Phone, Briefcase, Calendar, Globe } from "lucide-react";
import { toast } from "sonner";

export default function ContractorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/contractors/${id}`);
    setC(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { contractor_id: id, rating, comment });
      toast.success("Review posted");
      setComment("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post review");
    }
  };

  if (!c) return <div className="max-w-4xl mx-auto px-5 py-20 text-stone-500">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-10 py-10">
      <div className="bg-white border border-[#E2DCD0] rounded-xl overflow-hidden">
        <div className="h-48 wood-grain relative">
          {c.cover_image && <img src={c.cover_image} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="p-8">
          <div className="overline text-[#B85C47] mb-2">{c.category}</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{c.business_name}</h1>
          <div className="flex flex-wrap items-center gap-5 mt-3 text-sm text-stone-600">
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.location}</span>
            {c.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.phone}</span>}
            {c.website && (
              <a href={c.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#B85C47] font-semibold hover:underline">
                <Globe className="w-3.5 h-3.5" /> {c.website.replace(/^https?:\/\//, "")}
              </a>
            )}
            {c.years_experience > 0 && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {c.years_experience} years exp.</span>}
            {c.hourly_rate && <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> ${c.hourly_rate}/hr</span>}
            <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#B85C47] text-[#B85C47]" /> {c.avg_rating || "—"} ({c.review_count})</span>
          </div>
          <p className="mt-6 text-stone-700 leading-relaxed">{c.bio}</p>

          {c.portfolio_images?.length > 0 && (
            <div className="mt-8">
              <h2 className="overline text-[#B85C47] mb-3">Portfolio</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {c.portfolio_images.map((url, i) => (
                  <img key={i} src={url} alt="" className="rounded-lg w-full h-40 object-cover" />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-10">
        <h2 className="text-2xl font-bold tracking-tight mb-5" style={{ fontFamily: "Outfit" }}>Reviews</h2>
        {c.reviews?.length > 0 ? (
          <div className="space-y-3 mb-8">
            {c.reviews.map(r => (
              <div key={r.review_id} className="bg-white border border-[#E2DCD0] rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm">{r.author_name}</div>
                  <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#B85C47] text-[#B85C47]" : "text-stone-300"}`} />)}</div>
                </div>
                <p className="text-sm text-stone-700">{r.comment}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-stone-500 mb-6">No reviews yet — be the first.</p>}

        {user && user.user_id !== c.user_id && (
          <form onSubmit={submitReview} className="bg-white border border-[#E2DCD0] rounded-xl p-5 space-y-3">
            <div className="text-sm font-semibold">Leave a review</div>
            <div className="flex gap-1">
              {[1,2,3,4,5].map(n => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`review-rating-${n}`}>
                  <Star className={`w-6 h-6 ${n <= rating ? "fill-[#B85C47] text-[#B85C47]" : "text-stone-300"}`} />
                </button>
              ))}
            </div>
            <Textarea data-testid="review-comment-input" value={comment} onChange={e => setComment(e.target.value)} placeholder="Share your experience…" required />
            <Button data-testid="submit-review-btn" type="submit" className="bg-[#314A3E] hover:bg-[#243a30] text-white">Post review</Button>
          </form>
        )}
      </div>
    </div>
  );
}
